import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import {
  Plus, Pencil, Trash2, X, Check, BookOpen,
  Eye, EyeOff, GripVertical, ChevronRight, QrCode, Download
} from 'lucide-react';
import {
  DndContext, closestCenter, PointerSensor, useSensor, useSensors
} from '@dnd-kit/core';
import {
  arrayMove, SortableContext, useSortable, verticalListSortingStrategy
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import api from '../../api/client';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import { useAuth } from '../../context/AuthContext';

// ── Sortable row ─────────────────────────────────────
function SortableRow({ tut, onEdit, onDelete, onToggle, onOpenChapters, onDownloadQR }) {
  const { can } = useAuth();
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: tut.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    background: isDragging ? 'var(--orange-50)' : undefined,
  };

  const thumbUrl = tut.previewImage
    ? tut.previewImage
    : `https://swardhara-data-store.blr1.cdn.digitaloceanspaces.com/data/image/${encodeURIComponent(tut.key_value || 'VOL')}`;

  return (
    <tr ref={setNodeRef} style={style}>
      <td style={{ width: 36 }}>
        {can('tutorials','edit') && (
          <span {...attributes} {...listeners} style={{ cursor: 'grab', color: 'var(--grey-300)', display: 'flex' }}>
            <GripVertical size={16} />
          </span>
        )}
      </td>
      <td style={{ width: 48 }}>
        <img src={thumbUrl} alt="" style={{ width: 52, height: 36, objectFit: 'cover', borderRadius: 6, border: '1px solid var(--grey-100)' }}
          onError={e => { e.target.src = `https://via.placeholder.com/52x36/ffd0a0/c95605?text=VOL`; }} />
      </td>
      <td>
        <div style={{ fontWeight: 600, color: 'var(--grey-800)' }}>{tut.name}</div>
        {tut.description && <div style={{ fontSize: 12, color: 'var(--grey-400)', marginTop: 1 }}>{tut.description}</div>}
      </td>
      <td>
        <code style={{ fontSize: 11, background: 'var(--orange-50)', padding: '2px 8px', borderRadius: 4, color: 'var(--orange-600)', fontWeight: 600 }}>
          {tut.key_value || '—'}
        </code>
      </td>
      <td style={{ textAlign: 'center' }}>
        <span className="badge badge-blue">{tut.chapter_count ?? 0}</span>
      </td>
      <td style={{ fontWeight: 600, color: 'var(--orange-600)' }}>
        {Number(tut.cost) === 0 ? <span className="badge badge-green">Free</span> : `₹${tut.cost}`}
      </td>
      <td>
        <button
          className={`btn btn-sm ${tut.visible ? 'btn-secondary' : 'btn-danger'}`}
          style={{ padding: '4px 10px', fontSize: 11 }}
          onClick={() => can('tutorials','edit') && onToggle(tut, 'visible', !tut.visible)}
          disabled={!can('tutorials','edit')}
          title={tut.visible ? 'Visible' : 'Hidden'}
        >
          {tut.visible ? <Eye size={12} /> : <EyeOff size={12} />}
          <span>{tut.visible ? 'Visible' : 'Hidden'}</span>
        </button>
      </td>
      <td>
        <div style={{ display: 'flex', gap: 4 }}>
          <button className="btn btn-sm btn-secondary" onClick={() => onOpenChapters(tut)} style={{ gap: 4 }}>
            Chapters <ChevronRight size={12} />
          </button>
          {tut.key_value && (
            <button className="btn-icon" title="Download QR ZIP" onClick={() => onDownloadQR(tut)}>
              <QrCode size={14} />
            </button>
          )}
          {can('tutorials','edit') && (
            <button className="btn-icon" onClick={() => onEdit(tut)}><Pencil size={14} /></button>
          )}
          {can('tutorials','delete') && (
            <button className="btn-icon" onClick={() => onDelete(tut.id)}><Trash2 size={14} /></button>
          )}
        </div>
      </td>
    </tr>
  );
}

// ── Main component ───────────────────────────────────
export default function TutorialsPage() {
  const { can } = useAuth();
  const navigate = useNavigate();
  const [tutorials, setTutorials] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [modal, setModal]         = useState(false);
  const [editing, setEditing]     = useState(null);
  const [confirm, setConfirm]     = useState(null);
  const [showDisabled, setShowDisabled] = useState(false);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }));

  const { register, handleSubmit, reset, formState: { errors } } = useForm();

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/tutorials?includeDisabled=${showDisabled}`);
      setTutorials(data.data);
    } catch { toast.error('Failed to load tutorials'); }
    finally { setLoading(false); }
  }, [showDisabled]);

  useEffect(() => { load(); }, [load]);

  // ── Drag end ───────────────────────────────────────
  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = tutorials.findIndex(t => t.id === active.id);
    const newIndex = tutorials.findIndex(t => t.id === over.id);
    const reordered = arrayMove(tutorials, oldIndex, newIndex);
    setTutorials(reordered);
    try {
      await api.patch('/tutorials/reorder', { orderedIds: reordered.map(t => t.id) });
      toast.success('Order saved');
    } catch { toast.error('Failed to save order'); load(); }
  };

  // ── Toggle visible/disable ─────────────────────────
  const handleToggle = async (tut, field, value) => {
    try {
      await api.patch(`/tutorials/${tut.id}/toggle`, { field, value });
      setTutorials(prev => prev.map(t => t.id === tut.id ? { ...t, [field]: value ? 1 : 0 } : t));
    } catch { toast.error('Failed to update'); }
  };

  // ── Modal ──────────────────────────────────────────
  const openCreate = () => {
    setEditing(null);
    reset({ name:'', title_hindi:'', title_marathi:'', description:'', description_hindi:'', description_marathi:'',
            previewImage:'', previewLink:'', cost:'0.0', sort_no:1, key_value:'', visible:true, disable:false });
    setModal(true);
  };

  const openEdit = (tut) => {
    setEditing(tut);
    reset({ ...tut, visible: !!tut.visible, disable: !!tut.disable });
    setModal(true);
  };

  const onSubmit = async (values) => {
    try {
      if (editing) { await api.put(`/tutorials/${editing.id}`, values); toast.success('Tutorial updated'); }
      else         { await api.post('/tutorials', values); toast.success('Tutorial created'); }
      setModal(false); load();
    } catch (err) { toast.error(err.response?.data?.message || 'Error'); }
  };

  const doDelete = async (id) => {
    try { await api.delete(`/tutorials/${id}`); toast.success('Deleted'); setConfirm(null); load(); }
    catch (err) { toast.error(err.response?.data?.message || 'Cannot delete: has active chapters'); setConfirm(null); }
  };

  // ── QR bulk download ──────────────────────────────
  const downloadQR = async (tut) => {
    try {
      toast.loading(`Generating QR codes for ${tut.name}…`, { id: 'qr' });
      const resp = await api.get(`/qr/tutorial/${tut.id}/bulk`, { responseType: 'blob' });
      const url  = URL.createObjectURL(resp.data);
      const a    = document.createElement('a');
      a.href = url;
      a.download = `${tut.key_value}_QR_Codes.zip`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('QR ZIP downloaded', { id: 'qr' });
    } catch { toast.error('QR generation failed', { id: 'qr' }); }
  };

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Tutorial Volumes</h1>
          <p className="page-subtitle">Manage Swardhara curricula — drag rows to reorder</p>
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <label className="toggle">
            <input type="checkbox" checked={showDisabled} onChange={e => setShowDisabled(e.target.checked)} />
            <span className="toggle-track"><span className="toggle-thumb" /></span>
            <span style={{ fontSize: 13 }}>Show disabled</span>
          </label>
          {can('tutorials','create') && (
            <button className="btn btn-primary" onClick={openCreate}><Plus size={15} /> Add Tutorial</button>
          )}
        </div>
      </div>

      <div className="card">
        <div className="table-wrap">
          {loading ? <div className="loader-wrap"><div className="loader" /></div> : (
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
              <SortableContext items={tutorials.map(t => t.id)} strategy={verticalListSortingStrategy}>
                <table className="data-table">
                  <thead>
                    <tr>
                      <th style={{ width: 36 }}></th>
                      <th style={{ width: 60 }}>Thumb</th>
                      <th>Title</th>
                      <th>Key</th>
                      <th style={{ textAlign: 'center' }}>Chapters</th>
                      <th>Price</th>
                      <th>Visibility</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tutorials.map(tut => (
                      <SortableRow
                        key={tut.id} tut={tut}
                        onEdit={openEdit}
                        onDelete={id => setConfirm(id)}
                        onToggle={handleToggle}
                        onOpenChapters={tut => navigate(`/tutorials/${tut.id}/chapters`)}
                        onDownloadQR={downloadQR}
                      />
                    ))}
                    {!tutorials.length && (
                      <tr><td colSpan={8}>
                        <div className="empty-state"><BookOpen size={36} /><h3>No tutorials yet</h3><p>Click "Add Tutorial" to get started.</p></div>
                      </td></tr>
                    )}
                  </tbody>
                </table>
              </SortableContext>
            </DndContext>
          )}
        </div>
      </div>

      {/* ─── Create / Edit Modal ────────────── */}
      {modal && (
        <div className="modal-overlay" onClick={() => setModal(false)}>
          <div className="modal modal-lg" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <span className="modal-title">{editing ? 'Edit' : 'Add'} Tutorial Volume</span>
              <button className="btn-icon" onClick={() => setModal(false)}><X size={18} /></button>
            </div>
            <form onSubmit={handleSubmit(onSubmit)}>
              <div className="modal-body">
                {/* Tab-like sections */}
                <div style={{ marginBottom: 20 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--orange-600)', textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 12 }}>
                    English
                  </div>
                  <div className="form-group">
                    <label className="form-label">Title (English) <span className="req">*</span></label>
                    <input className={`form-control ${errors.name ? 'error' : ''}`} placeholder="e.g. Swardhara 1"
                      {...register('name', { required: 'Title is required' })} />
                    {errors.name && <p className="form-error">{errors.name.message}</p>}
                  </div>
                  <div className="form-group">
                    <label className="form-label">Description (English)</label>
                    <input className="form-control" placeholder="Brief description of this volume"
                      {...register('description')} />
                  </div>
                </div>

                <div className="form-grid" style={{ marginBottom: 20 }}>
                  <div>
                    <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--orange-600)', textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 12 }}>Hindi</div>
                    <div className="form-group">
                      <label className="form-label">Title (Hindi)</label>
                      <input className="form-control" {...register('title_hindi')} />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Description (Hindi)</label>
                      <textarea className="form-control" rows={2} {...register('description_hindi')} />
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--orange-600)', textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 12 }}>Marathi</div>
                    <div className="form-group">
                      <label className="form-label">Title (Marathi)</label>
                      <input className="form-control" {...register('title_marathi')} />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Description (Marathi)</label>
                      <textarea className="form-control" rows={2} {...register('description_marathi')} />
                    </div>
                  </div>
                </div>

                <div style={{ borderTop: '1px solid var(--grey-100)', paddingTop: 16, marginBottom: 16 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--orange-600)', textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 12 }}>
                    Media & Settings
                  </div>
                  <div className="form-group">
                    <label className="form-label">Preview Image URL (CDN)</label>
                    <input className="form-control" placeholder="https://cdn.../thumbnail.jpg" {...register('previewImage')} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Preview Video URL (CDN / M3U8)</label>
                    <input className="form-control" placeholder="https://cdn.../playlist.m3u8" {...register('previewLink')} />
                  </div>
                  <div className="form-grid cols-3">
                    <div className="form-group">
                      <label className="form-label">Price (₹)</label>
                      <input className="form-control" placeholder="999.0" {...register('cost')} />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Sort Order</label>
                      <input type="number" className="form-control" {...register('sort_no')} />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Key Value <span className="req">*</span></label>
                      <input className="form-control" placeholder="VOL01" style={{ textTransform: 'uppercase' }}
                        {...register('key_value')} />
                      <p style={{ fontSize: 11, color: 'var(--grey-400)', marginTop: 3 }}>Used for QR codes. Unique.</p>
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 24, marginTop: 8 }}>
                    <label className="toggle">
                      <input type="checkbox" {...register('visible')} />
                      <span className="toggle-track"><span className="toggle-thumb" /></span>
                      <span style={{ fontSize: 13 }}>Visible to users</span>
                    </label>
                    <label className="toggle">
                      <input type="checkbox" {...register('disable')} />
                      <span className="toggle-track"><span className="toggle-thumb" /></span>
                      <span style={{ fontSize: 13 }}>Disabled</span>
                    </label>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary"><Check size={14} /> Save Tutorial</button>
              </div>
            </form>
          </div>
        </div>
      )}

      <ConfirmDialog open={!!confirm} title="Delete Tutorial"
        message="All chapters must be disabled before deleting a tutorial."
        onConfirm={() => doDelete(confirm)} onCancel={() => setConfirm(null)} />
    </div>
  );
}
